import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { setAuthUser } from "@/lib/observability";

const CF_TEAM_DOMAIN = "https://ddaraban.cloudflareaccess.com";
const APP_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
// Chained logout: app logout clears the per-app CF_Authorization cookie, then
// redirects to team logout which clears the team SSO cookie, then back to /.
// Skipping the app step leaves the app cookie alive so the next page load
// silently re-authenticates and the user never sees the login screen.
const TEAM_LOGOUT_RETURN_HOME = `${CF_TEAM_DOMAIN}/cdn-cgi/access/logout?returnTo=${encodeURIComponent((APP_ORIGIN || "") + "/")}`;
const FULL_LOGOUT = `/cdn-cgi/access/logout?returnTo=${encodeURIComponent(TEAM_LOGOUT_RETURN_HOME)}`;
const APP_LOGOUT = "/cdn-cgi/access/logout";
const IDENTITY_ENDPOINT = "/cdn-cgi/access/get-identity";

export interface AuthUser {
  email: string;
  name?: string;
  id?: string;
  idpType?: string;
  groups?: string[];
  expiresAt?: number;
}

export type AuthStatus = "unknown" | "checking" | "authenticated" | "anonymous" | "error";

export interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  error?: string;
  refresh: () => Promise<void>;
  signIn: () => void;
  signOut: () => void;
  signOutAppOnly: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface IdentityResponse {
  id?: string;
  email?: string;
  name?: string;
  exp?: number;
  groups?: { id: string; name: string }[] | string[];
  idp?: { id?: string; type?: string };
}

function parseIdentity(raw: IdentityResponse | null): AuthUser | null {
  if (!raw || !raw.email) return null;
  const groups = Array.isArray(raw.groups)
    ? raw.groups.map(g => (typeof g === "string" ? g : g.name)).filter(Boolean)
    : undefined;
  return {
    email: String(raw.email).toLowerCase(),
    name: raw.name,
    id: raw.id,
    idpType: raw.idp?.type,
    groups,
    expiresAt: typeof raw.exp === "number" ? raw.exp * 1000 : undefined,
  };
}

async function fetchIdentity(signal?: AbortSignal): Promise<AuthUser | null> {
  const res = await fetch(IDENTITY_ENDPOINT, {
    method: "GET",
    credentials: "include",
    cache: "no-store",
    headers: { Accept: "application/json" },
    signal,
  });
  // 404 on the identity endpoint means there is no Access app in front of
  // the host (e.g. *.pages.dev preview deploys). Treat as anonymous.
  if (res.status === 401 || res.status === 403 || res.status === 302 || res.status === 404) {
    return null;
  }
  if (!res.ok) throw new Error(`identity_http_${res.status}`);
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.includes("application/json")) return null;
  const data = (await res.json()) as IdentityResponse;
  return parseIdentity(data);
}

function clearLocalSession() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem("aba-sync-state");
  } catch {
    // ignore
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("unknown");
  const [error, setError] = useState<string | undefined>(undefined);

  const refresh = useCallback(async () => {
    setStatus(prev => (prev === "authenticated" ? prev : "checking"));
    setError(undefined);
    try {
      const next = await fetchIdentity();
      setUser(next);
      setStatus(next ? "authenticated" : "anonymous");
    } catch (e: any) {
      setError(e?.message ?? "identity_error");
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchIdentity();
        if (cancelled) return;
        setUser(next);
        setStatus(next ? "authenticated" : "anonymous");
        setAuthUser(next);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ?? "identity_error");
        setStatus("error");
      }
    })();
    // Re-check identity when the tab regains focus, in case the Access session
    // expired in the background.
    const onFocus = () => refresh();
    // Re-check on BFCache restore so logout -> back-button doesn't leave the
    // app stuck on a cached "authenticated" view after the cookie is gone.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) refresh();
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [refresh]);

  const signOut = useCallback(() => {
    clearLocalSession();
    // Append a cache-buster to the final return URL so the browser hits CF
    // fresh after the cookie is cleared, instead of serving / from its cache
    // and never giving CF Access the chance to show the OTP screen.
    const returnHome = `${APP_ORIGIN || ""}/?_signedOut=${Date.now()}`;
    const teamLogoutWithReturn = `${CF_TEAM_DOMAIN}/cdn-cgi/access/logout?returnTo=${encodeURIComponent(returnHome)}`;
    window.location.href = `/cdn-cgi/access/logout?returnTo=${encodeURIComponent(teamLogoutWithReturn)}`;
  }, []);

  const signOutAppOnly = useCallback(() => {
    clearLocalSession();
    window.location.href = APP_LOGOUT;
  }, []);

  const signIn = useCallback(() => {
    clearLocalSession();
    // A fresh top-level navigation to the app root forces CF Access to
    // present the identity provider flow when there is no session cookie.
    window.location.assign("/");
  }, []);

  return (
    <AuthContext.Provider value={{ status, user, error, refresh, signIn, signOut, signOutAppOnly }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
